import React, { useState, useEffect, useCallback, useMemo } from "react";
import { XCircle, Download, CheckCircle2, Trash2 } from "lucide-react";
import { useDebounce } from "use-debounce";
import { toast } from "react-toastify";
import Pagination from "../../../../components/common/Pagination";
import CertificateRequestFilter from "./CertificateRequestFilter";
import {
  listCertificateRequests,
  approveCertificateRequestHq,
  approveCertificateRequestMgmt,
  rejectCertificateRequest,
  deleteCertificateRequest,
  getCertificateRequestById,
  getCertificatesForRequest,
  bulkApproveCertificateRequestsHq,
  bulkApproveCertificateRequestsMgmt,
} from "../../../../api/elearning/certificates/certificateRequests";
import { exportCertificates, getCertificateTemplates } from "../../../../api/elearning/certificates/certificates";
import CertificateRequestDetailModal from "./CertificateRequestDetailModal";
import CertificateRequestDeleteModal from "./CertificateRequestDeleteModal";
import CertificateRequestBulkApproveModal from "./CertificateRequestBulkApproveModal";
import CertificateDownloadQueuePanel from "./CertificateDownloadQueuePanel";
import { useCertificateDownloadQueue } from "../hooks/useCertificateDownloadQueue";
import {
  parseExportErrorMessage,
  triggerBlobDownload,
} from "../certificateDownloadUtils";
import { motion, AnimatePresence } from "framer-motion";

const STATUS_LABEL = {
  pending_hq: "Chờ duyệt chuyên môn",
  pending_mgmt: "Chờ duyệt lãnh đạo",
  completed: "Hoàn thành",
  rejected: "Từ chối",
};
const REQUEST_KIND_META = {
  student: {
    label: "Học viên theo lớp",
    className: "bg-blue-50 text-blue-700",
  },
  teacher: {
    label: "Giáo viên upload",
    className: "bg-violet-50 text-violet-700",
  },
};
const ROUTING_LABEL = {
  english: "Tuyến TA",
  skills: "Tuyến kỹ năng",
  general: "Tuyến chung",
};
const ZIP_DOWNLOAD_COOLDOWN_MS = 5 * 60 * 1000;
const CERT_REQUEST_DELETE_ADMIN_EMAIL = "administrator@hamesoft.com";
const BULK_APPROVE_HQ_MAX = 500;
const BULK_APPROVE_MGMT_MAX = 100;

/** Loại bỏ ký tự cấm tên file Windows; giữ dấu tiếng Việt và khoảng trắng trong từng phần. */
function cleanZipNameSegment(value, fallback) {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  const cleaned = raw
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || fallback;
}

function stripLopPrefix(value) {
  const raw = String(value || "").trim();
  const upper = raw.toLocaleUpperCase("vi-VN");
  if (upper.startsWith("LỚP ")) return raw.slice(4).trim();
  return raw;
}

function formatClassZipSegment(className) {
  const raw = cleanZipNameSegment(className, "");
  if (!raw) return "LỚP";
  const code = stripLopPrefix(raw);
  if (!code) return "LỚP";
  return `LỚP ${code}`;
}

function buildRequestZipFileName(row) {
  const branch = cleanZipNameSegment(row?.branch_name, "Chi nhánh");
  const school = cleanZipNameSegment(
    row?.school_name,
    row?.request_kind === "teacher" ? "Giáo viên" : "Trung tâm"
  );
  const classPart =
    row?.request_kind === "teacher"
      ? cleanZipNameSegment(row?.class_name, "GCN giáo viên")
      : formatClassZipSegment(row?.class_name);
  return `${branch}_${school}_${classPart}.zip`;
}

const formatCooldownClock = (remainingMs) => {
  const totalSeconds = Math.max(0, Math.ceil(Number(remainingMs || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("vi-VN");
};

const formatTeacherCertType = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getHqStageText = (row) => {
  if (row?.hq_approved_at) {
    return `C1: ${row?.hq_approver_name || "Đã duyệt"} • ${formatDateTime(row.hq_approved_at)}`;
  }
  if (row?.status === "rejected") return "C1: Quy trình đã dừng";
  return "C1: Chờ chuyên môn duyệt";
};

const getMgmtStageText = (row) => {
  if (row?.mgmt_approved_at) {
    return `C2: ${row?.mgmt_approver_name || "Đã duyệt"} • ${formatDateTime(row.mgmt_approved_at)}`;
  }
  if (row?.status === "pending_mgmt") return "C2: Chờ lãnh đạo duyệt";
  if (row?.status === "pending_hq") return "C2: Chưa tới bước duyệt";
  if (row?.status === "rejected") return "C2: Không tiếp tục do yêu cầu bị từ chối";
  return "C2: Chưa có dữ liệu";
};

const CertificateRequestQueue = ({
  onTemplates = () => {},
  showCreateRequestButton = false,
  onOpenRequestPicker,
  onChanged = () => {},
  showTemplatesButton = true,
  refreshSignal = 0,
}) => {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState("all");
  const [mineOnly, setMineOnly] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch] = useDebounce(searchInput, 400);
  const [busyId, setBusyId] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [templateOptions, setTemplateOptions] = useState([]);
  const downloadQueue = useCertificateDownloadQueue();
  const [zipCooldowns, setZipCooldowns] = useState({});
  const [cooldownTick, setCooldownTick] = useState(() => Date.now());
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectionMeta, setSelectionMeta] = useState({});
  const [approvalMeta, setApprovalMeta] = useState({
    can_approve_hq: false,
    can_approve_mgmt: false,
  });
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkApproving, setBulkApproving] = useState(false);
  const canApproveLevel1 = approvalMeta.can_approve_hq;
  const canApproveLevel2 = approvalMeta.can_approve_mgmt;
  const canBulkSelect = true;
  const canDeleteRequest = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return (
        String(user?.email || "").trim().toLowerCase() === CERT_REQUEST_DELETE_ADMIN_EMAIL
      );
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listCertificateRequests({
        page,
        size: pageSize,
        status: status === "all" ? undefined : status,
        mine: mineOnly ? "1" : undefined,
        keyword: debouncedSearch.trim() || undefined,
      });
      setRecords(data.records || []);
      setTotal(data.total || 0);
      setApprovalMeta({
        can_approve_hq: Boolean(data?.approval_meta?.can_approve_hq),
        can_approve_mgmt: Boolean(data?.approval_meta?.can_approve_mgmt),
      });
    } catch {
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, status, mineOnly, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!refreshSignal) return;
    setPage(1);
    load();
  }, [refreshSignal, load]);

  useEffect(() => {
    const activeCooldowns = Object.values(zipCooldowns || {}).some((expiresAt) => Number(expiresAt) > Date.now());
    if (!activeCooldowns) return undefined;
    const timer = window.setInterval(() => {
      setCooldownTick(Date.now());
      setZipCooldowns((prev) => {
        const next = Object.fromEntries(
          Object.entries(prev || {}).filter(([, expiresAt]) => Number(expiresAt) > Date.now())
        );
        return Object.keys(next).length === Object.keys(prev || {}).length ? prev : next;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [zipCooldowns]);

  const act = async (id, fn, okMsg) => {
    setBusyId(id);
    try {
      await fn(id);
      toast.success(okMsg);
      load();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.message || "Thao tác thất bại.");
    } finally {
      setBusyId(null);
    }
  };

  const loadStudentTemplates = useCallback(async () => {
    try {
      const d = await getCertificateTemplates("student");
      setTemplateOptions(Array.isArray(d.templates) ? d.templates : []);
    } catch {
      setTemplateOptions([]);
    }
  }, []);

  useEffect(() => {
    setSelectedIds([]);
    setSelectionMeta({});
  }, [page, pageSize, status, mineOnly, debouncedSearch]);

  useEffect(() => {
    if (!canApproveLevel1) return;
    loadStudentTemplates();
  }, [canApproveLevel1, loadStudentTemplates]);

  const isRowSelectable = useCallback(
    (row) => {
      if (!row?.id) return false;
      if (row.status === "completed") return true;
      if (canApproveLevel1 && row.status === "pending_hq") return true;
      if (canApproveLevel2 && row.status === "pending_mgmt") return true;
      return false;
    },
    [canApproveLevel1, canApproveLevel2]
  );

  const selectableOnPage = useMemo(
    () => records.filter(isRowSelectable),
    [records, isRowSelectable]
  );

  const allSelectableOnPageSelected = useMemo(() => {
    if (!selectableOnPage.length) return false;
    return selectableOnPage.every((r) => selectedIds.includes(r.id));
  }, [selectableOnPage, selectedIds]);

  const rememberSelectionMeta = useCallback((rows = []) => {
    if (!rows.length) return;
    setSelectionMeta((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        if (!row?.id) return;
        next[row.id] = {
          status: row.status,
          request_kind: row.request_kind || "student",
          branch_name: row.branch_name,
          school_name: row.school_name,
          class_name: row.class_name,
          student_count: row.student_count,
        };
      });
      return next;
    });
  }, []);

  const selectedHqIds = useMemo(
    () => selectedIds.filter((id) => selectionMeta[id]?.status === "pending_hq"),
    [selectedIds, selectionMeta]
  );

  const selectedMgmtIds = useMemo(
    () => selectedIds.filter((id) => selectionMeta[id]?.status === "pending_mgmt"),
    [selectedIds, selectionMeta]
  );

  const selectedHqRows = useMemo(
    () =>
      selectedHqIds.map((id) => ({
        id,
        request_kind: selectionMeta[id]?.request_kind || "student",
      })),
    [selectedHqIds, selectionMeta]
  );

  const selectedMgmtRows = useMemo(
    () => selectedMgmtIds.map((id) => ({ id })),
    [selectedMgmtIds]
  );

  const selectedCompletedIds = useMemo(
    () => selectedIds.filter((id) => selectionMeta[id]?.status === "completed"),
    [selectedIds, selectionMeta]
  );

  const buildRowFromSelectionMeta = useCallback(
    (id) => {
      const meta = selectionMeta[id] || {};
      const onPage = records.find((row) => row.id === id);
      if (onPage) return onPage;
      return {
        id,
        status: "completed",
        branch_name: meta.branch_name,
        school_name: meta.school_name,
        class_name: meta.class_name,
        student_count: meta.student_count,
      };
    },
    [records, selectionMeta]
  );

  const toggleRowSelection = (row, event) => {
    event?.stopPropagation?.();
    if (!row?.id) return;
    rememberSelectionMeta([row]);
    setSelectedIds((prev) =>
      prev.includes(row.id) ? prev.filter((x) => x !== row.id) : [...prev, row.id]
    );
  };

  const toggleSelectAllOnPage = () => {
    if (!selectableOnPage.length) return;
    rememberSelectionMeta(selectableOnPage);
    const pageIds = selectableOnPage.map((r) => r.id);
    setSelectedIds((prev) => {
      const hasAll = pageIds.every((id) => prev.includes(id));
      if (hasAll) return prev.filter((id) => !pageIds.includes(id));
      return Array.from(new Set([...prev, ...pageIds]));
    });
  };

  const fetchAllFilteredRequestRows = async (requiredStatus, limitMax) => {
    const baseParams = {
      size: 100,
      status: requiredStatus,
      mine: mineOnly ? "1" : undefined,
      keyword: debouncedSearch.trim() || undefined,
    };
    const first = await listCertificateRequests({ ...baseParams, page: 1 });
    const totalRows = Number(first?.total || 0);
    const maxRows = Math.min(totalRows, limitMax);
    let rows = (first?.records || []).filter(isRowSelectable);
    const totalPages = Math.ceil(maxRows / 100);

    for (let p = 2; p <= totalPages; p += 1) {
      const d = await listCertificateRequests({ ...baseParams, page: p });
      rows = rows.concat((d?.records || []).filter(isRowSelectable));
      if (rows.length >= maxRows) break;
    }
    return rows.slice(0, maxRows);
  };

  const handleSelectAllFiltered = async () => {
    if (status === "pending_hq" && canApproveLevel1) {
      try {
        const rows = await fetchAllFilteredRequestRows("pending_hq", BULK_APPROVE_HQ_MAX);
        rememberSelectionMeta(rows);
        setSelectedIds(rows.map((r) => r.id));
        toast.info(`Đã chọn ${rows.length} yêu cầu chờ duyệt cấp 1.`);
      } catch {
        toast.error("Không thể chọn theo bộ lọc.");
      }
      return;
    }
    if (status === "pending_mgmt" && canApproveLevel2) {
      try {
        const rows = await fetchAllFilteredRequestRows("pending_mgmt", BULK_APPROVE_MGMT_MAX);
        rememberSelectionMeta(rows);
        setSelectedIds(rows.map((r) => r.id));
        toast.info(`Đã chọn ${rows.length} yêu cầu chờ duyệt cấp 2.`);
      } catch {
        toast.error("Không thể chọn theo bộ lọc.");
      }
      return;
    }
    toast.warning(
      "Vui lòng lọc trạng thái «Chờ duyệt chuyên môn» hoặc «Chờ duyệt lãnh đạo» trước khi chọn theo bộ lọc."
    );
  };

  const confirmBulkApprove = async ({ templateId } = {}) => {
    if (!bulkModal) return;
    const mode = bulkModal;
    const ids = mode === "hq" ? selectedHqIds : selectedMgmtIds;
    if (!ids.length) {
      toast.warning("Không có yêu cầu phù hợp để duyệt.");
      return;
    }

    setBulkApproving(true);
    try {
      const result =
        mode === "hq"
          ? await bulkApproveCertificateRequestsHq({
              ids,
              template_id: templateId || undefined,
            })
          : await bulkApproveCertificateRequestsMgmt({ ids });

      const ok = Number(result?.succeeded || 0);
      const fail = Number(result?.failed || 0);
      if (ok > 0) {
        toast.success(result?.message || `Đã duyệt ${ok} yêu cầu.`);
      }
      if (fail > 0) {
        const firstFail = result?.failures?.[0]?.message;
        toast.warning(
          `${fail} yêu cầu không duyệt được${firstFail ? `: ${firstFail}` : "."}`
        );
      }
      if (ok === 0 && fail === 0) {
        toast.info(result?.message || "Không có yêu cầu nào được duyệt.");
      }

      setBulkModal(null);
      setSelectedIds([]);
      setSelectionMeta({});
      load();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.message || "Không thể duyệt hàng loạt.");
    } finally {
      setBulkApproving(false);
    }
  };

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const d = await getCertificateRequestById(id);
      setDetailData(d);
      if (d?.status === "pending_hq" && d?.request_kind !== "teacher" && templateOptions.length === 0) {
        await loadStudentTemplates();
      }
    } catch {
      toast.error("Không tải được chi tiết.");
      setDetailOpen(false);
      setDetailData(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const openDelete = (row) => {
    if (!row?.id) return;
    setDeleteTarget(row);
    setDeleteOpen(true);
  };

  const confirmDeleteRequest = async (deleteReason) => {
    if (!deleteTarget?.id) return;
    setBusyId(deleteTarget.id);
    try {
      await deleteCertificateRequest(deleteTarget.id, { delete_reason: deleteReason });
      toast.success("Đã xóa yêu cầu và ghi nhật ký.");
      setDeleteOpen(false);
      setDeleteTarget(null);
      if (detailData?.id === deleteTarget.id) {
        setDetailOpen(false);
        setDetailData(null);
      }
      load();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.message || "Không thể xóa yêu cầu.");
    } finally {
      setBusyId(null);
    }
  };

  const enqueueRequestZipDownload = useCallback(
    (row) => {
      const requestId = row?.id;
      if (!requestId) return;
      const jobId = `request-zip-${requestId}`;
      if (downloadQueue.isJobActive(jobId)) return;

      const fileName = buildRequestZipFileName(row);
      downloadQueue.enqueue({
        id: jobId,
        title: fileName,
        subtitle: `${row.student_count || 0} hồ sơ • #${String(requestId).slice(0, 8)}`,
        fileName,
        execute: async ({ onProgress }) => {
          try {
            const { certificates } = await getCertificatesForRequest(requestId);
            if (!certificates?.length) {
              throw new Error("Chưa có chứng nhận để tải.");
            }
            const ids = certificates.map((c) => c.id).filter(Boolean);
            onProgress({ phase: "preparing", totalItems: ids.length, progress: null });

            const res = await exportCertificates(ids, "zip", {
              onDownloadProgress: (event) => {
                const loadedBytes = Number(event?.loaded || 0);
                const totalBytes = Number(event?.total || 0);
                const progress =
                  totalBytes > 0
                    ? Math.max(0, Math.min(100, Math.round((loadedBytes / totalBytes) * 100)))
                    : null;
                onProgress({
                  phase: "downloading",
                  loadedBytes,
                  totalBytes,
                  progress,
                });
              },
            });

            onProgress({ phase: "saving", progress: 100 });
            triggerBlobDownload(res.data, fileName);
            setZipCooldowns((prev) => ({
              ...prev,
              [requestId]: Date.now() + ZIP_DOWNLOAD_COOLDOWN_MS,
            }));
          } catch (err) {
            const data = err?.response?.data;
            const nextAllowedAt =
              typeof data === "object" && data && !(data instanceof Blob)
                ? data.next_allowed_at
                : null;
            const retryAfterSeconds =
              typeof data === "object" && data && !(data instanceof Blob)
                ? data.retry_after_seconds
                : null;
            if (nextAllowedAt || Number.isFinite(Number(retryAfterSeconds))) {
              const expiresAt = nextAllowedAt
                ? new Date(nextAllowedAt).getTime()
                : Date.now() + Math.max(0, Number(retryAfterSeconds) || 0) * 1000;
              if (Number.isFinite(expiresAt) && expiresAt > Date.now()) {
                setZipCooldowns((prev) => ({
                  ...prev,
                  [requestId]: expiresAt,
                }));
              }
            }
            const message = await parseExportErrorMessage(
              err,
              "Không tải được file ZIP chứng nhận."
            );
            throw new Error(message);
          }
        },
      });
    },
    [downloadQueue]
  );

  const downloadRow = (row) => {
    enqueueRequestZipDownload(row);
  };

  const downloadSelectedCompleted = () => {
    if (!selectedCompletedIds.length) {
      toast.warning("Chọn ít nhất một yêu cầu đã hoàn thành để tải ZIP.");
      return;
    }
    selectedCompletedIds.forEach((id) => {
      enqueueRequestZipDownload(buildRowFromSelectionMeta(id));
    });
    toast.info(`Đã thêm ${selectedCompletedIds.length} tác vụ tải ZIP vào hàng chờ.`);
  };

  return (
    <div className="space-y-4">
      <CertificateRequestFilter
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        status={status}
        onStatusChange={(value) => {
          setStatus(value);
          setPage(1);
        }}
        mineOnly={mineOnly}
        onMineOnlyChange={(value) => {
          setMineOnly(Boolean(value));
          setPage(1);
        }}
        onRefresh={load}
        loading={loading}
        onTemplates={onTemplates}
        showTemplates={showTemplatesButton}
        showCreateRequestButton={showCreateRequestButton}
        onOpenRequestPicker={onOpenRequestPicker}
      />

      {canBulkSelect && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-gray-100 bg-white px-4 py-3">
          <button
            type="button"
            onClick={toggleSelectAllOnPage}
            disabled={loading || bulkApproving || !selectableOnPage.length}
            className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-black uppercase tracking-wide text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {allSelectableOnPageSelected ? "Bỏ chọn trang này" : "Chọn trang này"}
          </button>
          <button
            type="button"
            onClick={handleSelectAllFiltered}
            disabled={loading || bulkApproving}
            className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-black uppercase tracking-wide text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Chọn theo bộ lọc
          </button>
          <span className="ml-1 text-xs font-bold text-gray-500">
            Đã chọn: <span className="text-red-700">{selectedIds.length}</span>
            {selectedHqRows.length > 0 && (
              <span className="ml-2 text-emerald-700">C1: {selectedHqRows.length}</span>
            )}
            {selectedMgmtRows.length > 0 && (
              <span className="ml-2 text-red-700">C2: {selectedMgmtRows.length}</span>
            )}
            {selectedCompletedIds.length > 0 && (
              <span className="ml-2 text-blue-700">ZIP: {selectedCompletedIds.length}</span>
            )}
          </span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {selectedCompletedIds.length > 0 && (
              <button
                type="button"
                onClick={downloadSelectedCompleted}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-3 py-2 text-xs font-black uppercase tracking-wide text-white disabled:opacity-50"
              >
                <Download size={14} /> Tải ZIP đã chọn ({selectedCompletedIds.length})
              </button>
            )}
            {canApproveLevel1 && (
              <button
                type="button"
                onClick={() => setBulkModal("hq")}
                disabled={!selectedHqRows.length || bulkApproving}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-black uppercase tracking-wide text-white disabled:opacity-50"
              >
                <CheckCircle2 size={14} /> Duyệt cấp 1 ({selectedHqRows.length})
              </button>
            )}
            {canApproveLevel2 && (
              <button
                type="button"
                onClick={() => setBulkModal("mgmt")}
                disabled={!selectedMgmtRows.length || bulkApproving}
                className="inline-flex items-center gap-2 rounded-xl bg-red-700 px-3 py-2 text-xs font-black uppercase tracking-wide text-white disabled:opacity-50"
              >
                <CheckCircle2 size={14} /> Duyệt cấp 2 ({selectedMgmtRows.length})
              </button>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto relative min-h-[160px]">
          {loading && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
              <div className="animate-spin w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full" />
            </div>
          )}
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50/50 border-b border-gray-100">
              <tr>
                {canBulkSelect && (
                  <th className="p-4 w-12 text-center">
                    <input
                      type="checkbox"
                      checked={allSelectableOnPageSelected}
                      onChange={toggleSelectAllOnPage}
                      disabled={!selectableOnPage.length || bulkApproving}
                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-400"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                )}
                <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Mã / Thời gian</th>
                <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Nội dung yêu cầu</th>
                <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Người gửi / SL</th>
                <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Tiến độ xử lý</th>
                <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Tác vụ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {!loading &&
                records.map((r) => (
                  (() => {
                    const isRowDownloadBusy = downloadQueue.isJobActive(`request-zip-${r.id}`);
                    const cooldownUntil = Number(zipCooldowns[r.id] || 0);
                    const cooldownRemainingMs = cooldownUntil - cooldownTick;
                    const isZipCoolingDown = cooldownRemainingMs > 0;
                    const rowSelectable = isRowSelectable(r);
                    return (
                  <tr
                    key={r.id}
                    className="hover:bg-gray-50/50 cursor-pointer"
                    onClick={() => openDetail(r.id)}
                  >
                    {canBulkSelect && (
                      <td className="p-4 text-center align-top" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(r.id)}
                          disabled={!rowSelectable || bulkApproving}
                          onChange={(e) => toggleRowSelection(r, e)}
                          className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-400 disabled:opacity-40"
                        />
                      </td>
                    )}
                    <td className="p-4 min-w-[180px] align-top">
                      <p className="font-mono text-xs font-black uppercase tracking-wider text-red-700">
                        #{String(r.id || "").slice(0, 8) || "—"}
                      </p>
                      <p className="mt-2 text-sm font-semibold text-gray-800">
                        {formatDateTime(r.created_at)}
                      </p>
                      <p className="mt-1 text-[11px] text-gray-500">
                        {r.template_name ? `Mẫu: ${r.template_name}` : "Mẫu: sẽ chọn trong quy trình duyệt"}
                      </p>
                    </td>
                    <td className="p-4 min-w-[320px] align-top">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex rounded-lg px-2 py-1 text-[10px] font-black uppercase ${
                            REQUEST_KIND_META[r.request_kind]?.className || "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {REQUEST_KIND_META[r.request_kind]?.label || "Yêu cầu"}
                        </span>
                        {r.request_kind !== "teacher" && (
                          <span className="inline-flex rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-black uppercase text-slate-700">
                            {ROUTING_LABEL[r.routing_key] || "Tuyến chung"}
                          </span>
                        )}
                        {r.request_kind === "teacher" && !!r.teacher_cert_type && (
                          <span className="inline-flex rounded-lg bg-fuchsia-50 px-2 py-1 text-[10px] font-black uppercase text-fuchsia-700">
                            {formatTeacherCertType(r.teacher_cert_type)}
                          </span>
                        )}
                      </div>

                      <div className="mt-3 space-y-1">
                        <p className="text-sm font-bold text-gray-800">{r.class_name || "—"}</p>
                        <p className="text-[11px] text-gray-500">
                          {[r.branch_name, r.school_name].filter(Boolean).join(" • ") || "—"}
                        </p>
                        <p className="text-[11px] text-gray-600">
                          {[r.course_name, r.subject_name].filter(Boolean).join(" • ") ||
                            (r.request_kind === "teacher"
                              ? "Danh sách giáo viên gửi từ file upload"
                              : "Chưa có thông tin khóa học / môn học")}
                        </p>
                      </div>
                    </td>
                    <td className="p-4 min-w-[220px] align-top">
                      <p className="text-sm font-bold text-gray-800">{r.requester_name || "—"}</p>
                      <p className="mt-1 break-all text-[11px] text-gray-500">
                        {r.requester_email || "—"}
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="inline-flex rounded-lg bg-red-50 px-2.5 py-1 text-[10px] font-black uppercase text-red-700">
                          {r.student_count} hồ sơ
                        </span>
                        <span className="inline-flex rounded-lg bg-gray-100 px-2.5 py-1 text-[10px] font-black uppercase text-gray-700">
                          {r.template_name ? "Đã có mẫu" : "Chưa chọn mẫu"}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 min-w-[280px] align-top">
                      <span
                        className={`text-[10px] font-black uppercase px-2 py-1 rounded-lg ${
                          r.status === "completed"
                            ? "bg-emerald-50 text-emerald-700"
                            : r.status === "rejected"
                            ? "bg-red-50 text-red-700"
                            : "bg-amber-50 text-amber-800"
                        }`}
                      >
                        {STATUS_LABEL[r.status] || r.status}
                      </span>
                      <div className="mt-3 space-y-2">
                        <p className="text-[11px] leading-relaxed text-gray-600">
                          {getHqStageText(r)}
                        </p>
                        <p className="text-[11px] leading-relaxed text-gray-500">
                          {getMgmtStageText(r)}
                        </p>
                      </div>
                    </td>
                    <td className="p-4 align-top">
                      <div className="flex flex-wrap justify-end gap-1">
                        {r.status === "completed" && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadRow(r);
                            }}
                            disabled={isRowDownloadBusy || isZipCoolingDown}
                            className="p-1.5 text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                            title={
                              isRowDownloadBusy
                                ? "Đang trong hàng chờ tải"
                                : isZipCoolingDown
                                ? `Có thể tải lại sau ${formatCooldownClock(cooldownRemainingMs)}`
                                : "Tải giấy chứng nhận"
                            }
                          >
                            <Download size={16} />
                          </button>
                        )}
                        {canDeleteRequest && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDelete(r);
                            }}
                            disabled={busyId === r.id}
                            className="p-1.5 text-red-700 hover:bg-red-50 rounded-lg border border-red-100 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                            title="Xóa yêu cầu (quản trị)"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                    );
                  })()
                ))}
            </tbody>
          </table>
          {!loading && records.length === 0 && (
            <div className="p-12 text-center text-gray-400 text-sm font-bold">Không có yêu cầu.</div>
          )}
        </div>
        {total > 0 && (
          <div className="p-4 border-t border-gray-50">
            <Pagination
              totalRecords={total}
              currentPage={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>

      <AnimatePresence>
        {rejectOpen && (
          <div className="fixed inset-0 z-[260] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-900/50"
              onClick={() => setRejectOpen(null)}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative bg-white rounded-2xl p-6 max-w-md w-full shadow-xl z-[270]"
            >
              <h3 className="font-black text-gray-800 mb-3">Từ chối yêu cầu</h3>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Lý do (tuỳ chọn)"
                className="w-full border border-gray-200 rounded-xl p-3 text-sm min-h-[88px] mb-4"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRejectOpen(null)}
                  className="px-4 py-2 rounded-xl font-bold text-gray-500"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!rejectOpen) return;
                    setBusyId(rejectOpen);
                    try {
                      await rejectCertificateRequest(rejectOpen, { reason: rejectReason });
                      toast.success("Đã từ chối yêu cầu.");
                      setRejectOpen(null);
                      load();
                      onChanged();
                      if (detailData?.id === rejectOpen) {
                        try {
                          const d = await getCertificateRequestById(rejectOpen);
                          setDetailData(d);
                        } catch {}
                      }
                    } catch (err) {
                      toast.error(err.response?.data?.message || "Không thể từ chối.");
                    } finally {
                      setBusyId(null);
                    }
                  }}
                  className="px-4 py-2 rounded-xl font-bold bg-red-700 text-white flex items-center gap-2"
                >
                  <XCircle size={16} /> Xác nhận từ chối
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <CertificateDownloadQueuePanel
        jobs={downloadQueue.jobs}
        onDismiss={downloadQueue.clearFinished}
        title="Hàng chờ tải ZIP"
      />

      <CertificateRequestDeleteModal
        open={deleteOpen}
        requestLabel={
          deleteTarget?.id ? `#${String(deleteTarget.id).slice(0, 8)}` : ""
        }
        busy={!!deleteTarget && busyId === deleteTarget.id}
        onClose={() => {
          if (busyId === deleteTarget?.id) return;
          setDeleteOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={confirmDeleteRequest}
      />

      <CertificateRequestDetailModal
        open={detailOpen}
        detail={detailData}
        busy={detailLoading || (!!detailData && busyId === detailData.id)}
        canDeleteRequest={canDeleteRequest || Boolean(detailData?.can_delete_request)}
        templateOptions={templateOptions}
        onClose={() => {
          setDetailOpen(false);
          setDetailData(null);
        }}
        canApproveHq={detailData?.can_approve_hq ?? canApproveLevel1}
        canApproveMgmt={detailData?.can_approve_mgmt ?? canApproveLevel2}
        onApproveHq={(templateId) =>
          detailData &&
          act(
            detailData.id,
            (id) => approveCertificateRequestHq(id, { template_id: templateId }),
            "Đã duyệt cấp 1 (chuyên môn)."
          ).then(async () => {
            try {
              const d = await getCertificateRequestById(detailData.id);
              setDetailData(d);
            } catch {}
          })
        }
        onApproveMgmt={() =>
          detailData &&
          act(
            detailData.id,
            approveCertificateRequestMgmt,
            "Đã duyệt cấp 2 — chứng nhận đã được cấp."
          ).then(async () => {
            try {
              const d = await getCertificateRequestById(detailData.id);
              setDetailData(d);
            } catch {}
          })
        }
        onReject={() => {
          if (!detailData?.id) return;
          setRejectOpen(detailData.id);
          setRejectReason("");
        }}
        onDelete={() => {
          if (!detailData?.id) return;
          openDelete(detailData);
        }}
      />

      <CertificateRequestBulkApproveModal
        open={Boolean(bulkModal)}
        mode={bulkModal || "hq"}
        count={bulkModal === "hq" ? selectedHqRows.length : selectedMgmtRows.length}
        studentCount={selectedHqRows.filter((r) => r.request_kind !== "teacher").length}
        teacherCount={selectedHqRows.filter((r) => r.request_kind === "teacher").length}
        templateOptions={templateOptions}
        busy={bulkApproving}
        onClose={() => !bulkApproving && setBulkModal(null)}
        onConfirm={confirmBulkApprove}
      />

    </div>
  );
};

export default CertificateRequestQueue;
